import axios from "axios";
import { useEffect, useState } from "react";
import {
  CheckCircle,
  Eye,
  EyeSlash,
  Lock,
  XCircle,
} from "react-bootstrap-icons";
import { useParams } from "react-router-dom";

import "./LoginPage.css";
import { ApiConnectionString } from "../../constants/ApiConntection";

export default function LoginPage({ onLogin }) {
  const { slug } = useParams();

  const [therapist, setTherapist] = useState(null);
  const [isTherapistLoading, setIsTherapistLoading] = useState(true);
  const [therapistErr, setTherapistErr] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotErr, setForgotErr] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const fetchTherapist = async () => {
      if (!slug) {
        setTherapistErr("Practice link is missing.");
        setIsTherapistLoading(false);
        return;
      }

      try {
        setIsTherapistLoading(true);
        setTherapistErr("");

        const response = await axios.get(
          `${ApiConnectionString}/api/client/therapist/${slug}`
        );

        const data = response.data;

        const therapistId =
          data.id ||
          data.Id ||
          data.therapistId ||
          data.TherapistId ||
          data.appUserTherapistId ||
          data.AppUserTherapistId;

        setTherapist({
          id: therapistId,
          practiceName: data.practiceName || data.PracticeName,
          firstName: data.firstName || data.FirstName,
          lastName: data.lastName || data.LastName,
        });
      } catch (error) {
        console.error(error);

        if (error.response?.status === 404) {
          setTherapistErr(
            "This therapist practice could not be found or is no longer active."
          );
        } else {
          setTherapistErr(
            "Unable to load the therapist practice. Please try again."
          );
        }
      } finally {
        setIsTherapistLoading(false);
      }
    };

    fetchTherapist();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();

    setValidated(true);
    setErr("");

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      return;
    }

    if (!slug) {
      setErr("Practice link is missing.");
      return;
    }

    if (!therapist) {
      setErr("The therapist practice could not be loaded.");
      return;
    }

    try {
      setIsLoading(true);

      const login = {
        email: email.trim().toLowerCase(),
        password,
      };

      const response = await axios.post(
        `${ApiConnectionString}/api/login/${slug}`,
        login,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const clientUser = {
          token: response.data.token || response.data.Token,
          id: response.data.id || response.data.Id,
          name: response.data.name || response.data.Name,
          slug,
        };

        localStorage.setItem("clientUser", JSON.stringify(clientUser));

        onLogin(clientUser);
      }
    } catch (error) {
      console.error(error);

      const responseError = error.response?.data;

      if (Array.isArray(responseError)) {
        setErr(responseError[0]);
      } else if (typeof responseError === "string") {
        setErr(responseError);
      } else {
        setErr("Login failed. Please check your details and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendForgotPassword = async (e) => {
    e.preventDefault();

    setForgotErr("");
    setForgotSuccess("");

    const cleanEmail = forgotEmail.trim().toLowerCase();

    if (!cleanEmail) {
      setForgotErr("Enter your email address.");
      return;
    }

    if (!therapist) {
      setForgotErr("The therapist practice could not be loaded.");
      return;
    }

    if (!therapist.id) {
      setForgotErr(
        "Password reset is unavailable because the therapist ID was not returned."
      );
      return;
    }

    try {
      setForgotLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/ForgotPassword?email=${encodeURIComponent(
          cleanEmail
        )}&therapistId=${therapist.id}`,
        {}
      );

      setForgotSuccess(
        "If this email matches an account, a reset link has been sent."
      );

      setForgotEmail("");
    } catch (error) {
      console.error(error);

      if (error.response?.status === 429) {
        setForgotErr(
          "Too many password reset requests. Please try again later."
        );
      } else if (typeof error.response?.data === "string") {
        setForgotErr(error.response.data);
      } else {
        setForgotErr("Could not send the reset email. Please try again.");
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const therapistName = therapist
    ? `${therapist.firstName || ""} ${therapist.lastName || ""}`.trim()
    : "";

  return (
    <div className="login-page">
      <div className="login-card">
        <section className="login-visual">
          <div className="login-visual-content">
            <div className="login-visual-icon">
              <Lock />
            </div>

            <h1>Therapy Diary App</h1>

            {isTherapistLoading ? (
              <p>Loading practice...</p>
            ) : therapist ? (
              <>
                {therapist.practiceName && (
                  <h2>{therapist.practiceName}</h2>
                )}

                {therapistName && <p>{therapistName}</p>}

                <span>Client Portal</span>
              </>
            ) : (
              <p>Practice unavailable</p>
            )}
          </div>
        </section>

        <section className="login-form">
          <span className="login-badge">
            <Lock />
            Secure client login
          </span>

          <h2>Welcome back</h2>

          <p className="login-intro">
            {therapistName
              ? `Log in to view your appointments and messages with ${therapistName}.`
              : "Log in to view your appointments and messages with your therapist."}
          </p>

          {therapistErr && (
            <div className="login-error">
              <XCircle />
              <span>{therapistErr}</span>
            </div>
          )}

          {err && (
            <div className="login-error">
              <XCircle />
              <span>{err}</span>
            </div>
          )}

          <form
            className={validated ? "login-main-form was-validated" : "login-main-form"}
            onSubmit={submit}
            noValidate
          >
            <div className="login-field">
              <label htmlFor="client-email">Email address</label>

              <input
                id="client-email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!therapist || isTherapistLoading}
                autoComplete="email"
                required
              />

              <div className="login-field-error">
                Enter a valid email address.
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="client-password">Password</label>

              <div className="password-field">
                <input
                  id="client-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!therapist || isTherapistLoading}
                  autoComplete="current-password"
                  required
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((current) => !current)}
                  disabled={!therapist || isTherapistLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </button>
              </div>

              <div className="login-field-error">Enter your password.</div>
            </div>

            <div className="login-row">
              <label className="remember-row">
                <input
                  type="checkbox"
                  disabled={!therapist || isTherapistLoading}
                />
                <span>Remember me</span>
              </label>

              <button
                type="button"
                className="text-button"
                disabled={!therapist || isTherapistLoading}
                onClick={() => {
                  setForgotOpen(true);
                  setForgotEmail(email);
                  setForgotErr("");
                  setForgotSuccess("");
                }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="primary-btn login-submit"
              disabled={isLoading || isTherapistLoading || !therapist}
            >
              {isLoading ? "Logging in..." : "Log in"}
            </button>
          </form>

          {forgotOpen && (
            <div className="forgot-box">
              <div className="forgot-heading">
                <div>
                  <h3>Reset password</h3>
                  <p>
                    Enter your email address and we will send you a password
                    reset link.
                  </p>
                </div>

                <button
                  type="button"
                  className="forgot-close"
                  onClick={() => {
                    setForgotOpen(false);
                    setForgotErr("");
                    setForgotSuccess("");
                  }}
                  aria-label="Close password reset"
                >
                  ×
                </button>
              </div>

              {forgotErr && (
                <div className="login-error">
                  <XCircle />
                  <span>{forgotErr}</span>
                </div>
              )}

              {forgotSuccess && (
                <div className="login-success">
                  <CheckCircle />
                  <span>{forgotSuccess}</span>
                </div>
              )}

              <form onSubmit={sendForgotPassword}>
                <div className="login-field">
                  <label htmlFor="forgot-email">Email address</label>

                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="primary-btn login-submit"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "Sending..." : "Send reset link"}
                </button>
              </form>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}